import React, { createContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { deleteVideoSession, setPlayerPlaybackError, updateWatchingInProgress, updateVideoSession, setPlayerReady } from 'store/actions';
import throttle from 'lodash/throttle';

// import shaka-player
import shaka from 'shaka-player/dist/shaka-player.ui.js';

// Import helpers
import { INITIAL_STATE_PLAYER_VOD, TIME_THROTTLE, calculateVideoIntervalTime } from '../helpers';
import { playerDeleteSyncRequest } from 'helpers/player';
import { CUSTOM_ERROR } from 'helpers/error_code_api';

// styles and helpers
import {
	VideoContainer,
	ShakaVideo,
	removeShakaPlayerListeners,
	playerInitialization,
	setSupportError,
	setTrackingEvent,
	sendPlayerEvents,
	sendPreviousSessionTracking
} from '../shakaPlayer';

// Variables
import { LOCALSTORAGE_PLAYER, STRIPES_URL, TRACKING_EVENTS } from 'helpers/variables';

// Create context
export const ShakaPlayerContext = createContext();

const { SHAKA_PLAYER_ERROR_MESSAGE } = CUSTOM_ERROR;

class ShakaPlayerProvider extends React.Component {
	_isMounted = false;
	isPlayerReady = false;
	intervalUpdateWatchingProgress = null;
	intervalVideoSession = null;
	trackingInterval = null;
	streamStartDate = null;

	constructor(props) {
		super(props);
		this.state = INITIAL_STATE_PLAYER_VOD;
		this.timeThrottle = throttle((time) => this.handleSetCurrentTime(time), TIME_THROTTLE);

		//Creating reference to store video component and video container
		this.videoComponentRef = React.createRef();
		this.videoContainerRef = React.createRef();

		// Shaka media
		this.video = null;
		this.videoContainer = null;
		this.player = null;
	}

	async componentDidMount() {
		const {
			isError,
			videoSessionId,
			isSiedebarVisible,
			configuration: { movieEndMargin },
			isPreview
		} = this.props;

		!isPreview && this.checkFirstStripe();

		this._isMounted = true;
		this.setState({ isSiedebarVisible, movieEndMargin });

		// send previous session tracking data
		sendPreviousSessionTracking();

		// Delete video sesion
		window.addEventListener('beforeunload', this.beforeunload, false);

		if (videoSessionId && this._isMounted && !this.isPlayerReady && !isError) {
			// Install built-in polyfills to patch browser incompatibilities.
			shaka.polyfill.installAll();
			shaka.polyfill.PatchedMediaKeysApple.install();

			//Getting reference to video and video container on DOM
			this.video = this.videoComponentRef.current;
			this.videoContainer = this.videoContainerRef.current;

			shaka.Player.isBrowserSupported() ? playerInitialization.call(this, [shaka]) : setSupportError.call(this);
		} else if (isError && videoSessionId) {
			// If there is an error and video session is not equal to null
			// Turn on buffering
			this._isMounted && this.setState({ buffering: false });
		} else if (isError && !videoSessionId) {
			// If there is an error and video session is equal to null
			// Stop player
			this.player && this.player.destroy();
			// Turn on buffering,Set player ready
			this._isMounted && this.setState({ buffering: false, isReady: false });
		}
	}

	componentWillUnmount() {
		// Update watching progress time
		this.handleFetchWatchingProgress();

		this._isMounted = false;

		// Clear intervals
		clearInterval(this.intervalVideoSession);
		clearInterval(this.intervalUpdateWatchingProgress);
		clearInterval(this.trackingInterval);

		const { isError, videoSessionId, type, deleteVideoSession } = this.props;
		// Check if player is not undefined / null, if player container exists, if is not any API errors

		if (videoSessionId) {
			// Dispatch an deleteVideoSession action in player folder
			deleteVideoSession({ type });
		}

		if (!isError && this.player && this.isPlayerReady) {
			// send tracking events
			setTrackingEvent.call(this, TRACKING_EVENTS.CLOSED);
			sendPlayerEvents.call(this);
			// remove event listeners
			removeShakaPlayerListeners.call(this);
			// Destroy the player
			this.player.destroy();
			this.isPlayerReady = false;
		}
	}

	handleFetchWatchingProgress = (currentProgress = null) => {
		const { isPreview } = this.props;
		const { isReady } = this.state;
		const isAvaialbe = this._isMounted && isReady && this.video?.currentTime;

		if (isAvaialbe && !isPreview) {
			const currentTime = currentProgress || this.video.currentTime;
			const time = Math.floor(currentTime);
			this.props.updateWatchingInProgress({
				productUuid: this.props.productID,
				time
			});
		}
	};

	beforeunload = () => {
		const { videoSessionId } = this.props;
		// delete session
		playerDeleteSyncRequest(videoSessionId);
	};

	setFullScreen = () => {
		if (!document.fullscreenElement) {
			this.videoContainer.requestFullscreen();
			this.setState({ isFullScreen: true });
		} else {
			document.exitFullscreen();
			this.setState({ isFullScreen: false });
		}
	};

	play = () => {
		this.video.play();
		this.setState({ isPaused: this.video.paused });
	};

	pause = () => {
		this.video.pause();
		this.setState({ isPaused: this.video.paused });
	};

	updateTime = (time) => {
		this.video.currentTime = time;
		setTrackingEvent.call(this, TRACKING_EVENTS.SEEKING);
	};

	updateSeekTime = (next = true) => {
		const { seekSec } = this.state;
		const currentTime = this.video.currentTime;

		next ? this.updateTime(currentTime + seekSec) : this.updateTime(currentTime - seekSec);
	};

	// Mute or unmute player
	mute = () => {
		const playerStorage = JSON.parse(localStorage.getItem(LOCALSTORAGE_PLAYER)) || {};
		const { isMuted } = this.state;

		if (!isMuted) {
			this.setState({ isMuted: true, volume: 0 });
		} else {
			if (playerStorage?.volume) {
				const volume = playerStorage.volume;
				this.video.volume = volume / 100;
				this.setState({ volume });
			}
			this.setState({ isMuted: false });
		}

		localStorage.setItem(LOCALSTORAGE_PLAYER, JSON.stringify({ ...playerStorage, isMuted: !isMuted }));
	};

	// Set current volume
	setVolume = (volume) => {
		const playerStorage = JSON.parse(localStorage.getItem(LOCALSTORAGE_PLAYER)) || {};
		const isMuted = parseInt(volume) === 0;
		localStorage.setItem(LOCALSTORAGE_PLAYER, JSON.stringify({ ...playerStorage, volume, isMuted }));

		this.video.volume = volume / 100;
		this.setState({ volume, isMuted });
	};

	onReady = () => {
		const {
			type,
			setPlayerReady,
			configuration: { videoSession }
		} = this.props;

		// Set player ready
		this.isPlayerReady = true;
		this.setState({ isReady: true });
		// Dispatch an setPlayerReady action in player folder
		setPlayerReady({ type });

		// Set watching in progress interval
		const watchingIntervalTime = parseInt(process.env.REACT_APP_WATCHING_IN_PROGRESS);
		this.intervalUpdateWatchingProgress = setInterval(this.handleFetchWatchingProgress, watchingIntervalTime);
		// Set video session interval
		const videoSessionInvervalTime = calculateVideoIntervalTime(videoSession);
		this.intervalVideoSession = setInterval(this.handleUpdateVideoSession, videoSessionInvervalTime);
	};

	setInitialVolume = () => {
		const playerStorage = JSON.parse(localStorage.getItem(LOCALSTORAGE_PLAYER)) || {};
		const { isMuted, volume: storedVolume } = playerStorage;

		let volume = storedVolume ? storedVolume / 100 : this.video.volume;

		if (isMuted) {
			this.setState({ isMuted: true });
			volume = 0;
		}

		this.video.volume = volume;
		this.setState({ volume: volume * 100 });
	};

	handleUpdateVideoSession = () => {
		const { videoSessionId, type, productID, updateVideoSession } = this.props;

		updateVideoSession({ videoSessionId, productID, type }, () => {
			// When is an api error
			// Destroy the player
			this.player.destroy();
			// Turn on buffering
			this.setState({ buffering: false, isReady: false });
			// Clear intervals
			clearInterval(this.intervalVideoSession);
			clearInterval(this.intervalUpdateWatchingProgress);
		});
	};

	onPlay = () => {
		this.setState({ isPaused: this.video.paused });
		setTrackingEvent.call(this, TRACKING_EVENTS.PLAYING);
	};

	onPause = () => {
		this.setState({ isPaused: this.video.paused });
		setTrackingEvent.call(this, TRACKING_EVENTS.PAUSED);
	};

	onBufferStateChange = () => {
		const buffering = this.player.isBuffering();
		this.setState({ buffering });
		buffering && setTrackingEvent.call(this, TRACKING_EVENTS.BUFFERING);
	};

	onEnded = () => {
		this.setState({ isEnded: true, isPaused: true });
		setTrackingEvent.call(this, TRACKING_EVENTS.COMPLETE);
	};

	onAutoQualityChange = () => setTrackingEvent.call(this, TRACKING_EVENTS.TRACK_CHANGED);

	onErrorEvent = (event) => {
		// Extract the shaka.util.Error object from the event.
		this.onError(event.detail);
	};

	onError = (error) => {
		const isOnline = navigator.onLine;

		if (this && this.props) {
			const { setPlayerPlaybackError, type } = this.props;

			setTrackingEvent.call(this, TRACKING_EVENTS.ERROR);

			// Catch only known errors which should be handled in UI
			if (error && error.code && isOnline) {
				this.setState({ isReady: false, buffering: false });
				// Destroy the player
				if (this.player) {
					this.player.destroy();
				}
				// Set error message
				error.message = SHAKA_PLAYER_ERROR_MESSAGE;
				// Dispatch an setPlayerPlaybackError action in player folder
				setPlayerPlaybackError(error, type);
				// Set player ready / Turn on buffering
				// Clear intervals
				clearInterval(this.intervalVideoSession);
				clearInterval(this.intervalUpdateWatchingProgress);
			}
		}
	};

	onFullScreenChange = () => {
		const isFullScreen = !!document.fullscreenElement;
		this.setState({ isFullScreen });
	};

	getDuration = () => {
		const { isReady, duration } = this.state;

		const isAvailable = this._isMounted && isReady && this.video && duration;
		return isAvailable ? this.video.duration : null;
	};

	handleSetCurrentTime = (time) => {
		const { isReady } = this.state;
		const isAvaialbe = this._isMounted && this.video && isReady;
		// Get duration
		if (isAvaialbe) {
			const duration = this.getDuration();
			if (duration && this.state.duration !== duration) {
				this.setState({ duration });
			}

			// Set end margin action
			const { endMarginAction, movieEndMargin } = this.state;
			const endMargin = duration - movieEndMargin;

			if (duration && time > endMargin && !endMarginAction) {
				this.setState({ endMarginAction: true });
			} else if (duration && time < endMargin && endMarginAction) {
				this.setState({ endMarginAction: false });
			}
		}
	};

	currentTimeProgress = (callback) => this.video.addEventListener('timeupdate', () => callback(this.video.currentTime));

	restart = () => {
		this.video.currentTime = 0;
		this.setState({ isEnded: false });
	};

	onTimeUpdate = () => this.timeThrottle(this.video.currentTime);

	setEndMargin = (value = true) => {
		this.setState({ endMarginAction: value, endMarginActionCanceled: value });
	};

	checkFirstStripe = () => {
		const {
			configuration: { videoId },
			productID
		} = this.props;

		const url = `${STRIPES_URL}/${productID}/${videoId}/stripes/stripe_0.jpg`;
		const firstStripe = new Image();

		firstStripe.onerror = () => this.setState({ isPreviewAvailable: false });
		firstStripe.onload = () => this.setState({ isPreviewAvailable: true });
		firstStripe.src = url;
	};

	onVariantChanged = () => {
		setTrackingEvent.call(this, TRACKING_EVENTS.TRACK_CHANGED);
	};

	render() {
		const {
			closePreview,
			isPreview,
			isHls,
			isFocus,
			children,
			resetFocus,
			productID,
			configuration: { videoId }
		} = this.props;
		const { isReady, isMuted, subtitles } = this.state;

		const currentTime = this.video ? this.video.currentTime : 0;

		return (
			<ShakaPlayerContext.Provider
				value={{
					...this.state,
					isPreview,
					isHls,
					closePreview,
					setVolume: this.setVolume,
					mute: this.mute,
					currentTime,
					updateTime: this.updateTime,
					updateSeekTime: this.updateSeekTime,
					setFullScreen: this.setFullScreen,
					play: this.play,
					pause: this.pause,
					restart: this.restart,
					currentTimeProgress: this.currentTimeProgress,
					setEndMargin: this.setEndMargin,
					resetFocus,
					productID,
					videoId,
					video: this.video,
					player: this.player
				}}
			>
				<VideoContainer className="video-container" ref={this.videoContainerRef} isReady={isReady} isFocus={isFocus}>
					<ShakaVideo className="shaka-video" id="shaka-video" ref={this.videoComponentRef} autoPlay={true} muted={isMuted} playsInline>
						{isHls &&
							subtitles.map(({ id, language, url }) => <track key={id} label="Subtitles" kind="subtitles" srcLang={language} src={url} default="" />)}
					</ShakaVideo>
					{children}
				</VideoContainer>
			</ShakaPlayerContext.Provider>
		);
	}
}

ShakaPlayerProvider.defaultProps = {
	isSiedebarVisible: false,
	isHls: false
};

ShakaPlayerProvider.propTypes = {
	isSiedebarVisible: PropTypes.bool,
	playlist: PropTypes.shape({
		drm: PropTypes.object,
		sources: PropTypes.object,
		subtitles: PropTypes.array
	}),
	isError: PropTypes.bool.isRequired,
	videoSessionId: PropTypes.string,
	deleteVideoSession: PropTypes.func.isRequired,
	type: PropTypes.string.isRequired,
	productID: PropTypes.string.isRequired,
	configuration: PropTypes.object.isRequired,
	closePreview: PropTypes.func.isRequired,
	isPreview: PropTypes.bool.isRequired,
	isFocus: PropTypes.bool.isRequired,
	resetFocus: PropTypes.func.isRequired,
	isHls: PropTypes.bool.isRequired
};

const mapStateToProps = (state, { type }) => {
	const { movieDetails, continue_watching } = state;

	const {
		playlist,
		configuration,
		error: { isError },
		videoSessionId
	} = state[type];

	const productID = movieDetails?.data?.uuid;

	const time = continue_watching?.continueProgress?.time || 0;

	return {
		playlist,
		productID,
		isError,
		configuration,
		videoSessionId,
		continueWatchingTime: time
	};
};

export default connect(mapStateToProps, {
	deleteVideoSession,
	setPlayerPlaybackError,
	updateWatchingInProgress,
	updateVideoSession,
	setPlayerReady
})(ShakaPlayerProvider);
